<template>
  <v-container
    fluid
    fill-height
    class="py-0"
  >
    <v-row>
      <v-col class="pt-0 pt-sm-3">
        <v-card>
          <v-card-title>
            Accounts
          </v-card-title>

          <v-card-text>
            <div class="v-data-table theme--light">
              <div class="v-data-table__wrapper">
                <table class=" data-table-td-caption">
                  <thead class="v-data-table-header">
                    <tr dense>
                      <template v-for="item in sortFields">
                        <th
                          :key="item.propName"
                          class="text-left"
                        >
                          {{ item.displayName }}
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <thead v-show="!accountsLoaded">
                    <tr class="v-datatable__progress">
                      <th
                        colspan="100%"
                        class="column"
                      >
                        <v-progress-linear indeterminate />
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    is="transition-group"
                    v-if="accountsLoaded"
                    name="flip-list"
                  >
                    <template v-for="item in accountsSorted">
                      <tr
                        :key="item['.key']"
                        :class="{'grey lighten-3': selectedAccountId === item['.key']}"
                        class="cursor-pointer"
                        style="transition: transform 0.5s; will-change: transform;"
                        @click="selectedAccountId = item['.key']"
                      >
                        <td>
                          <v-tooltip
                            bottom
                            class="text-no-wrap font-size-13"
                          >
                            <template #activator="{ on }">
                              <span v-on="on">
                                {{ item.name }}
                              </span>
                            </template>
                            <span>
                              AccountId: {{ item['.key'] }}
                            </span>
                          </v-tooltip>
                        </td>
                        <td>{{ item.shortName }}</td>
                        <td>
                          <a :href="`mailto:${item.email}`">
                            {{ item.email.replace(item.domain, '') }}
                          </a>
                        </td>
                        <td>{{ item.domain }}</td>
                        <td>{{ item.type }}</td>
                        <td>
                          {{ item.active }}
                          <!--
                        TODO MVP implement account deactivate

                        <v-switch
                        :disabled="item.associations[$store.state.user.uid].association === 'Self'"
                        input-value="item.active"
                        hide-details
                        small
                        @click="toggleActive "
                      /> -->
                        </td>
                        <td class="text-no-wrap">
                          <v-tooltip bottom>
                            <template #activator="{ on }">
                              <span v-on="on">
                                {{ $formatFsTimestamp(item.created) || '' }}
                              </span>
                            </template>
                            <span>{{ item.created && item.created.toDate().toLocaleString() || '' }}</span>
                          </v-tooltip>
                        </td>
                        <td>{{ usersById[item.createdBy] ? usersById[item.createdBy].name : '' }}</td>
                        <td class="text-no-wrap">
                          <v-tooltip bottom>
                            <template #activator="{ on }">
                              <span v-on="on">
                                {{ $formatFsTimestamp(item.modified) || '' }}
                              </span>
                            </template>
                            <span>{{ item.modified && item.modified.toDate().toLocaleString() || '' }}</span>
                          </v-tooltip>
                        </td>
                        <td>{{ usersById[item.modifiedBy] ? usersById[item.modifiedBy].name : '' }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="display-flex align-center">
              <v-switch
                id="showInactives"
                v-model="showInactives"
                :disabled="!inactivesCount"
                hide-details
                small
                class="pl-2 mx-1 my-1"
              >
                <span
                  slot="label"
                  :class="{'secondary--text' : showInactives}"
                >
                  {{ showInactivesLabel }}
                </span>
              </v-switch>
              <v-spacer />
              <add-new-button
                title="Add account"
                @click="addAccountDialog = true"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <Users
        v-if="$route.path === '/oladmin/accounts/users'"
        association="Colleague"
        association-title="Users"
        association-title-singular="User"
      />
      <Users
        v-if="showCounterpartyTable && $route.path === '/oladmin/accounts/users'"
        association="Counterparty"
        association-title="Counterparties"
        association-title-singular="Counter Party"
      />
      <Users
        v-if="$route.path === '/oladmin/accounts/clients'"
        association="Client"
        association-title="Clients"
        association-title-singular="Client"
      />
    </v-row>
    <AddAccountDialog
      :add-account-dialog="addAccountDialog"
      @actionClick="addAccount"
      @cancelClick="addAccountDialog = false"
    />

    <v-snackbar
      v-model="showSnackbar"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
      <v-btn
        text
        @click.native="showSnackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>

import { auth, fsdb } from '../firebase'
import orderBy from 'lodash/orderBy'
import Users from './Users'
import AddNewButton from '@/components/AddNewButton'
import { isStandardAccount } from '@/dataAccess/accountTypes'

const AddAccountDialog = () => import('@/components/AddAccountDialog')
const _ = { orderBy }

export default {
  name: 'Accounts',
  components: { Users, AddAccountDialog, AddNewButton },
  firestore() {
    return {
      accounts: fsdb.collection('accounts'),
      users: fsdb.collection('users')
    }
  },
  data: function () {
    return {
      showInactives: false,
      snackbar: { color: '', text: '' },
      showSnackbar: false,
      addAccountDialog: false,
      selectedAccount: {},
      selectedAccountId: null,
      sortFields: [
        { displayName: 'Name', propName: 'name', desc: false },
        { displayName: 'Short Name', propName: 'shortName', desc: false },
        { displayName: 'Contact', propName: 'email', desc: false },
        { displayName: 'Domain', propName: 'domain', desc: false },
        { displayName: 'Type', propName: 'type', desc: false },
        { displayName: 'Active', propName: 'active', desc: false },
        { displayName: 'Created', propName: 'created', desc: false },
        { displayName: 'Created By', propName: 'createdBy', desc: false },
        { displayName: 'Modified', propName: 'modified', desc: false },
        { displayName: 'Modified By', propName: 'modifiedBy', desc: false }
      ]
    }
  },
  computed: {
    accountsLoaded() { return this.accounts.length > 0 },
    accountsFiltered() { return this.accounts.filter(item => item.type !== 'individual' && (item.active || this.showInactives)) },
    accountsSorted() {
      var sortFields = this.sortFields.reduce((acc, item, index) => {
        acc.names.push(item.propName)
        acc.orders.push(item.desc ? 'desc' : 'asc')
        return acc
      }, { names: [], orders: [] })
      return _.orderBy(this.accountsFiltered, sortFields.names, sortFields.orders)
    },
    inactivesCount() {
      return this.accounts.filter(item => !item.active).length
    },
    accountsById() {
      return this.accounts.reduce((acc, item) => {
        item.id = item['.key']
        acc[item['.key']] = item
        return acc
      }, {})
    },
    showInactivesLabel() { return 'Show Inactives (' + this.inactivesCount + ')' },
    usersById() {
      return this.users.reduce((acc, item) => {
        acc[item['.key']] = item
        return acc
      }, {})
    },
    showCounterpartyTable() {
      return isStandardAccount(this.userAccount.type)
    }
  },
  watch: {
    /*     userAccount: {
      handler: function (newVal, oldVal) {
        if (newVal && newVal.id) {
          this.selectedAccount = newVal
        }
      },
      immediate: true,
      deep: true
    }, */
    selectedAccountId: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$store.commit('set_selectedAccountId', newVal)
        this.$store.commit('set_accountBinding', null)
        this.$store.commit('set_accountUsersBinding', null)
        this.$store.commit('set_clientUsersBinding', null) // TODO ALPHA should other objs be reset when this happens? Applies to all bindings
        this.$store.commit('set_pendingClientUsersBinding', null)
        this.$store.commit('set_inactiveClientUsersBinding', null)
        this.$store.dispatch('get_account')
      }
    }
    /*     selectedAccount: {
      handler: function (newVal, oldVal) {
        if (newVal && newVal.id) {
          // this.$store.commit('set_selectedAccountId', newVal.id)
          this.$store.dispatch('get_account', newVal.id)
          this.$store.commit('set_account', newVal)
          this.$store.commit('set_accountUsersBinding', null)
          this.$store.commit('set_clientUsersBinding', null)
        }
      },
      deep: true
    } */
  },
  beforeCreate() {
    this.dispatchActions(['get_account'])
    this.mapStates(['user', 'appUser', ['userAccount', 'account']])
  },
  async mounted() {
    await this.$store.state.get_accountBinding
    this.selectedAccountId = this.$store.state.selectedAccountId
  },
  methods: {
    /*     selectAccount (id) {
      this.selectedAccount = this.accountsById[id]
    }, */
    async addAccount(details) {
      // TODO BETA need to validate that account doesn't exist (create an "Add Account" endpoint with check)
      details.type = this.toCamelCase(details.type)
      details.active = true
      details.modifiedBy = this.$store.state.user.uid
      details.email = details.email.toLowerCase()
      details.domain = details.domain.toLowerCase()

      if (details.type === 'singleUser' || details.type === 'standardBusiness') {
        try {
          await this.$firePost('accounts', auth, {
            name: details.name,
            email: details.email,
            domain: details.domain,
            shortName: details.shortName,
            type: details.type
          })
          this.snackbar = { color: 'success', text: 'Account added.' }
          this.addAccountDialog = false
        } catch (e) {
          this.snackbar = {
            color: 'error',
            text: 'Error adding account. Try again.'
          }
          this.$Sentry.captureException(e)
        }
      } else {
        try {
          await fsdb.collection('accounts').add(details)
          this.snackbar = { color: 'success', text: 'Account added.' }
          this.addAccountDialog = false
        } catch (e) {
          this.snackbar = {
            color: 'error',
            text: 'Error adding account. Try again.'
          }
          this.$Sentry.captureException(e)
        }
      }

      this.showSnackbar = true
    },
    toCamelCase(str) {
      return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase()
      }).replace(/\s+/g, '')
    }
  }
}

</script>
